/**
 * API Communication Layer
 * Handles all HTTP requests to the backend API
 */

/** Default timeout for API requests (10 seconds) */
const DEFAULT_TIMEOUT = 10000;

/**
 * Fetch with timeout support
 * Wraps native fetch with AbortController for timeout handling
 *
 * @param {string} url - Request URL
 * @param {RequestInit} options - Fetch options
 * @param {number} [timeout=DEFAULT_TIMEOUT] - Timeout in milliseconds
 * @returns {Promise<Response>} Fetch response
 * @throws {Error} Throws 'Request timeout' on timeout
 */
async function fetchWithTimeout(url, options = {}, timeout = DEFAULT_TIMEOUT) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    throw error;
  }
}

// Export for use in other modules
export { fetchWithTimeout, DEFAULT_TIMEOUT };

/**
 * Get the appropriate API base URL based on environment
 * @returns {string} The API base URL
 */
const getAPIBaseURL = () => {
    // In production, use relative path (same origin)
    if (window.location.hostname !== 'localhost') {
        return '/api';
    }
    // In development, use absolute URL for cross-port communication
    return 'http://localhost:5000/api';
};

/**
 * Get the appropriate EventSource base URL based on environment
 * EventSource requires absolute URLs when connecting across different ports (dev)
 * @returns {string} The EventSource base URL (empty string in production for relative URLs)
 */
const getEventSourceBaseURL = () => {
    // In production, use relative path (same origin)
    if (window.location.hostname !== 'localhost') {
        return '';
    }
    // In development, use absolute URL for cross-port communication
    return 'http://localhost:5000';
};

const API_URL = getAPIBaseURL();
const EVENTSOURCE_BASE_URL = getEventSourceBaseURL();

// Export for use in other modules
export { API_URL, EVENTSOURCE_BASE_URL, getAPIBaseURL };

/**
 * Unwrap a standardized `{ success: true, data: ... }` backend envelope.
 * Returns `result.data` when the envelope is present; otherwise returns `result` as-is.
 * Pure function - no network I/O.
 *
 * @param {Object} result - Parsed JSON response body
 * @returns {any} Unwrapped data or the raw result
 */
export function parseApiEnvelope(result) {
  if (result?.success === true && result?.data !== undefined) {
    return result.data;
  }
  return result;
}

/**
 * Fetch wrapper that always includes credentials and sets Content-Type: application/json.
 * Used internally by DiscoveryAPI._request.
 *
 * @param {string} url
 * @param {RequestInit} [options={}]
 * @returns {Promise<Response>}
 */
export async function credentialedFetch(url, options = {}) {
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    },
    credentials: 'include'
  });
}

export class API {
  /**
   * Helper method to handle standardized API responses
   * Unwraps the data from { success: true, data: {...} } format
   */
  static async handleResponse(response) {
    try {
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API Error ${response.status}: ${errorText}`);
        try {
          const errorData = JSON.parse(errorText);
          return errorData;
        } catch {
          return { success: false, error: 'Server error occurred' };
        }
      }

      const result = await response.json();
      return parseApiEnvelope(result);
    } catch (error) {
      console.error('Error parsing response:', error);
      return null;
    }
  }

  /**
   * `handleResponse` plus HTTP status preservation, for the session
   * create/close call sites only.
   *
   * Those two are the paths where the caller must act on *which* failure it was
   * (a guest hitting `GUEST_ACCOUNT_REQUIRED` needs the conversion overlay; a
   * 429 needs a retry affordance) rather than just "it failed". `handleResponse`
   * already returns the parsed error body — which carries `code` — but drops the
   * status, so callers cannot tell a 403 from a 429. Deliberately not applied
   * globally: other callers inspect returned error bodies and must keep their
   * current shape.
   *
   * @param {Response} response - Fetch response
   * @returns {Promise<Object|null>} Parsed body, with `httpStatus` stamped on failures
   */
  static async handleResponseWithStatus(response) {
    const parsed = await this.handleResponse(response);
    if (parsed && typeof parsed === 'object' && (!response.ok || parsed.success === false)) {
      parsed.httpStatus = response.status;
    }
    return parsed;
  }

  /**
   * Send a message and return streamUrl for ResponseHandler consumption.
   * Returns the raw { streamUrl, jobId } for ResponseHandler.handleResponse().
   * @param {string} sessionId - Session ID
   * @param {string} message - User message
   * @param {Object} [metadata=null] - Optional metadata to include
   * @returns {Promise<Object>} ResponseHandler-compatible { data: { streamUrl, jobId } }
   * @throws {Error} When the request fails or the response is missing stream identifiers.
   */
  static async sendMessageForStream(sessionId, message, metadata = null) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sessionId,
          message,
          ...(metadata && { metadata })
        })
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Message stream request failed (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData?.error || errorData?.message || errorMessage;
        } catch {
          if (errorText) errorMessage = errorText;
        }
        throw new Error(errorMessage);
      }

      const result = await this.handleResponse(response);
      if (!result?.streamUrl || !result?.jobId) {
        throw new Error('Message stream response missing streamUrl or jobId');
      }

      // Re-wrap for ResponseHandler compatibility.
      // API.handleResponse() unwraps { success, data } → data (giving us { streamUrl, jobId }).
      // ResponseHandler.handleResponse() expects { data: { streamUrl } } — so we re-wrap.
      return { data: { streamUrl: result.streamUrl, jobId: result.jobId } };
    } catch (error) {
      console.error('Network error in sendMessageForStream:', error);
      throw error;
    }
  }

  static async pollJobStatus(jobId) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/jobs/${jobId}`, {
        credentials: 'include'
      });
      if (response.status === 401) {
        return { authExpired: true };
      }
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in pollJobStatus:', error);
      return null;
    }
  }

  /**
   * Close a session (user-initiated wrap-up)
   * Used when user finishes a conversation without starting a new topic.
   * Backend queues session processing for summary generation.
   *
   * @param {string} sessionId - Session ID to close
   * @returns {Promise<Object|null>} Close result { closed, status, processingQueued } or null on error
   */
  static async closeSession(sessionId) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/sessions/${sessionId}/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      const parsed = await this.handleResponseWithStatus(response);
      console.log('[OrientationClose:Frontend] API.closeSession response', {
        sessionId,
        httpStatus: response.status,
        body: parsed,
        step: 'api_close_response'
      });
      return parsed;
    } catch (error) {
      console.error('[OrientationClose:Frontend] API.closeSession threw', {
        sessionId,
        error: error?.message,
        stack: error?.stack,
        step: 'api_close_threw'
      });
      return null;
    }
  }

  /**
   * Extract advice handoff context from a TREATMENT session.
   * Used for directed Treatment → Advice transitions (reverse of the retired build handoff).
   *
   * @param {string} sessionId - Source TREATMENT session ID
   * @returns {Promise<{conversationThread: string|null, lastUserMessage: string|null}|null>}
   *   Handoff payload (either field may be null) or null on error
   */
  static async getAdviceHandoff(sessionId) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/sessions/${sessionId}/advice-handoff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getAdviceHandoff:', error);
      return null;
    }
  }

  /**
   * Stop a running job. Callers treat `result?.unsent === true` as the
   * queued-unsend contract; successful results also expose response metadata
   * usage as `result.usage` so the composer can clear a quota lock.
   *
   * @param {string} jobId - The job ID to stop
   * @returns {Promise<Object|null>} Response or null on error
   */
  static async stopJob(jobId) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/jobs/${jobId}/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (!response.ok) return await this.handleResponse(response);

      const envelope = await response.json();
      const data = parseApiEnvelope(envelope);
      if (!data || typeof data !== 'object') return data;
      return {
        ...data,
        ...(envelope.metadata?.usage && { usage: envelope.metadata.usage })
      };
    } catch (error) {
      // Fire-and-forget: log but don't throw
      console.warn('[API] stopJob error (non-blocking):', error.message);
      return null;
    }
  }

  /**
   * Create a new session (session-per-mode architecture)
   * Backend automatically closes any previous active sessions
   *
   * @param {string} mode - Session mode (ADVICE, DISCOVERY, TREATMENT, INTAKE)
   * @param {Object} contextParams - Optional mode-specific parameters
   * @returns {Promise<Object|null>} Session data { session, message, closedSessions } or null on error
   */
  static async createSession(mode = 'ADVICE', contextParams = {}) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/sessions/new`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          mode,
          metadata: contextParams
        })
      });

      return await this.handleResponseWithStatus(response);
    } catch (error) {
      console.error('Network error in createSession:', error);
      return null;
    }
  }

  /**
   * Flag the given session as private (Privacy Mode V1). Enable-only — there is
   * no un-set path — via the dedicated server-owned route (not the generic
   * metadata PATCH, which rejects `privacyMode` as a protected key).
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object|null>} `{ sessionId, privacyMode: true }` or null on error/failure
   */
  static async setSessionPrivacy(sessionId) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/sessions/${sessionId}/privacy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errorCode = null;
        try {
          errorCode = JSON.parse(errorText)?.error?.code || null;
        } catch { /* non-JSON error body */ }
        const err = new Error(`Failed to set session privacy (${response.status})`);
        err.code = errorCode;
        err.httpStatus = response.status;
        throw err;
      }
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in setSessionPrivacy:', error);
      throw error;
    }
  }

  /**
   * Same-click-mistake affordance: revert privacy mode if no messages were
   * sent since it was enabled. Never throws — a failure (network error, or a
   * 400 because real activity occurred) just means "not reverted," and the
   * caller falls back to the normal exit-confirmation flow.
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object|null>} `{ sessionId, privacyMode: false, reverted: true }` on success, or null
   */
  static async cancelSessionPrivacy(sessionId) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/sessions/${sessionId}/privacy/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (!response.ok) return null;
      return await this.handleResponse(response);
    } catch (error) {
      console.warn('Network error in cancelSessionPrivacy:', error);
      return null;
    }
  }

  /**
   * Update session metadata
   * Used to set questionnaire context for template-based messages
   * @param {string} sessionId - Session ID
   * @param {Object} metadata - Metadata to merge into session
   * @param {boolean} [metadata.startingQuestionnaire] - Flag for questionnaire intro template
   * @param {boolean} [metadata.resumingQuestionnaire] - Flag for questionnaire resume template
   * @param {Object} [metadata.currentQuestionnaire] - Current questionnaire details
   * @param {Object} [metadata.completedQuestionnaire] - Just-completed questionnaire details
   * @param {Object} [metadata.nextQuestionnaire] - Next questionnaire details
   * @param {boolean} [metadata.pathComplete] - Flag for path completion template
   * @returns {Promise<Object|null>} Updated metadata or null on error
   */
  static async updateSessionMetadata(sessionId, metadata) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/sessions/${sessionId}/metadata`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ metadata })
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in updateSessionMetadata:', error);
      return null;
    }
  }

  // TODO: Wire to new insights API — old /api/user-insights endpoints were removed in redesign

  /**
   * Record a button interaction as a message
   * @param {string} sessionId - Session ID
   * @param {string} buttonLabel - Button label text
   * @param {string} buttonAction - Button action (e.g., "switch_mode:DISCOVERY")
   * @param {string} [buttonGroupId] - Optional button group ID
   * @param {Object} [metadata] - Optional additional metadata
   * @returns {Promise<Object|null>} Created message data or null on error
   */
  static async recordButtonInteraction(sessionId, buttonLabel, buttonAction, buttonGroupId = null, metadata = {}) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/chat/button-interaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sessionId,
          buttonLabel,
          buttonAction,
          buttonGroupId,
          metadata
        })
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in recordButtonInteraction:', error);
      return null;
    }
  }

  // ===== AUTHENTICATION METHODS =====

  /**
   * Get available authentication providers
   * @returns {Promise<Object>} Provider configuration
   */
  static async getAuthProviders() {
    try {
      const response = await fetchWithTimeout(`${API_URL}/auth/providers`, {
        credentials: 'include'
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getAuthProviders:', error);
      return null;
    }
  }

  /**
   * Return beta admission status for the current browser session.
   * @returns {Promise<Object|null>}
   */
  static async getBetaAccessStatus() {
    try {
      const response = await fetchWithTimeout(`${API_URL}/auth/beta-access/status`, {
        credentials: 'include'
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getBetaAccessStatus:', error);
      return null;
    }
  }

  /**
   * Exchange plaintext beta access for identifier-only server session state.
   * @param {string} code
   * @returns {Promise<Object|null>}
   */
  static async validateBetaAccess(code) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/auth/beta-access/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code })
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in validateBetaAccess:', error);
      return null;
    }
  }

  /**
   * Login with email and password
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise<Object>} User data on success
   */
  static async login(email, password) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in login:', error);
      return null;
    }
  }

  /**
   * Initiate registration with email verification
   * Step 1 of registration flow - sends verification code to email
   * @param {Object} data - Registration data
   * @param {string} data.email - User's email
   * @param {string} data.password - User's password
   * @param {string} data.firstName - User's first name
   * @returns {Promise<Object>} { verificationId, maskedEmail, expiresIn } on success
   */
  static async registerInitiate(data) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/auth/register/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data)
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in registerInitiate:', error);
      return null;
    }
  }

  /**
   * Verify registration code and complete account creation
   * Step 2 of registration flow - verifies code and creates account
   * @param {Object} data - Verification data
   * @param {string} data.verificationId - Verification ID from initiate step
   * @param {string} data.code - 6-digit verification code
   * @returns {Promise<Object>} User data on success
   */
  static async registerVerify(data) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/auth/register/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data)
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in registerVerify:', error);
      return null;
    }
  }

  /**
   * Resend verification code
   * @param {Object} data - Resend data
   * @param {string} data.verificationId - Verification ID from initiate step
   * @returns {Promise<Object>} { remainingResends } on success
   */
  static async registerResend(data) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/auth/register/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data)
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in registerResend:', error);
      return null;
    }
  }

  /**
   * Logout current user
   * @returns {Promise<Object>} Logout confirmation
   */
  static async logout() {
    try {
      const response = await fetchWithTimeout(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in logout:', error);
      return null;
    }
  }

  /**
   * Request password reset email
   * @param {string} email - User's email address
   * @returns {Promise<Object>} Success message (always returns success to prevent enumeration)
   */
  static async passwordForgot(email) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/auth/password/forgot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email })
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in passwordForgot:', error);
      return null;
    }
  }

  /**
   * Reset password with token
   * @param {string} token - Reset token from email link
   * @param {string} newPassword - New password (min 6 characters)
   * @returns {Promise<Object>} Success message or error
   */
  static async passwordReset(token, newPassword) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/auth/password/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, newPassword })
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in passwordReset:', error);
      return null;
    }
  }

  /**
   * Update the authenticated user's editable profile fields.
   * Send any subset of { firstName, name }; at least one is required.
   * @param {Object} updates
   * @param {string} [updates.firstName]
   * @param {string|null} [updates.name]
   * @returns {Promise<Object>} Normalized response from handleResponse
   */
  static async updateProfile(updates) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/auth/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in updateProfile:', error);
      return null;
    }
  }

  /**
   * Update the authenticated user's email notification preferences.
   * Send any subset of { systemEmails, marketingEmails }; at least one is required.
   *
   * Like every API method here, this NEVER rejects: check
   * `!result || result.success === false` rather than wrapping it in try/catch.
   *
   * @param {Object} updates
   * @param {boolean} [updates.systemEmails] - Product & activity email
   * @param {boolean} [updates.marketingEmails] - Promotional email
   * @returns {Promise<Object>} Normalized response; `notificationPreferences` on success
   */
  static async updateNotificationPreferences(updates) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/auth/notification-preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in updateNotificationPreferences:', error);
      return null;
    }
  }

  /**
   * Change the authenticated user's password in-place.
   * @param {string} currentPassword
   * @param {string} newPassword - Min 8 characters
   * @returns {Promise<Object>} Normalized response from handleResponse
   */
  static async passwordChange(currentPassword, newPassword) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/auth/password/change`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ currentPassword, newPassword })
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in passwordChange:', error);
      return null;
    }
  }

  /**
   * Check current authentication status
   * @returns {Promise<Object>} User data if authenticated
   */
  static async checkAuth() {
    try {
      const response = await fetchWithTimeout(`${API_URL}/auth/me`, {
        credentials: 'include'
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in checkAuth:', error);
      return null;
    }
  }

  // ===== GUEST USER METHODS =====

  /**
   * Get current guest status
   * Returns guest-specific information or indicates user is authenticated
   * @returns {Promise<Object>} Guest status { isGuest, userId, firstName, onboardingStep, messages, expiresAt }
   */
  static async getGuestStatus() {
    try {
      const response = await fetchWithTimeout(`${API_URL}/guest/status`, {
        credentials: 'include'
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getGuestStatus:', error);
      return null;
    }
  }

  /**
   * Initialize a guest session
   * Creates a new guest user if none exists
   * @returns {Promise<Object>} Guest initialization result with status
   */
  static async initGuestSession() {
    try {
      const response = await fetchWithTimeout(`${API_URL}/guest/init`, {
        method: 'POST',
        credentials: 'include'
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in initGuestSession:', error);
      return null;
    }
  }

  /**
   * Capture guest user's first name during orientation
   * @param {string} firstName - User's first name
   * @returns {Promise<Object|null>} Updated user data or null on error
   */
  static async captureGuestName(firstName) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/guest/onboarding/name`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ firstName })
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in captureGuestName:', error);
      return null;
    }
  }

  /**
   * Update guest onboarding step and data
   * @param {string} step - Current onboarding step identifier
   * @param {Object} data - Onboarding data to merge
   * @returns {Promise<Object|null>} Updated state or null on error
   */
  static async updateGuestOnboardingStep(step, data = {}) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/guest/onboarding/step`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ step, data })
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in updateGuestOnboardingStep:', error);
      return null;
    }
  }

  /**
   * Mark guest onboarding as complete
   * @returns {Promise<Object|null>} Completion confirmation or null on error
   */
  static async completeGuestOnboarding() {
    try {
      const response = await fetchWithTimeout(`${API_URL}/guest/onboarding/complete`, {
        method: 'POST',
        credentials: 'include'
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in completeGuestOnboarding:', error);
      return null;
    }
  }

  /**
   * Dismiss/pause orientation — preserves onboardingStep, user can resume via dashboard card.
   * @returns {Promise<Object|null>}
   */
  static async dismissGuestOnboarding() {
    try {
      const response = await fetchWithTimeout(`${API_URL}/guest/onboarding/dismiss`, {
        method: 'POST',
        credentials: 'include'
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in dismissGuestOnboarding:', error);
      return null;
    }
  }

  /**
   * Clear orientation dismissal — user is resuming via the dashboard card.
   * @returns {Promise<Object|null>}
   */
  static async resumeGuestOnboarding() {
    try {
      const response = await fetchWithTimeout(`${API_URL}/guest/onboarding/resume`, {
        method: 'POST',
        credentials: 'include'
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in resumeGuestOnboarding:', error);
      return null;
    }
  }

  // ===== SESSION HISTORY METHODS =====

  /**
   * Get sessions for current user
   * @param {Object} options - Query options
   * @param {string} [options.type] - Filter by session type (e.g., 'ADVICE')
   * @param {number} [options.limit=100] - Max sessions to return
   * @param {number} [options.offset=0] - Pagination offset
   * @returns {Promise<Object|null>} Sessions list or null on error
   */
  static async getSessions(options = {}) {
    try {
      const { limit = 100, offset = 0, type } = options;
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      });
      // Add type filter if specified (e.g., 'ADVICE' for history page)
      if (type) {
        params.append('type', type);
      }
      const response = await fetchWithTimeout(
        `${API_URL}/chat/sessions?${params.toString()}`,
        { credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getSessions:', error);
      return null;
    }
  }

  /**
   * Get all messages for a specific session
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object|null>} Messages or null on error
   */
  static async getSessionMessages(sessionId) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/sessions/${sessionId}/messages`,
        { credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getSessionMessages:', error);
      return null;
    }
  }

  /**
   * Fetch session info, including the full `metadata` object (stance,
   * stanceSource, attunementCompletedAt, …). Used on ADVICE resume so the voice
   * chip can reflect an already-committed voice instead of showing neutral.
   * @param {string} sessionId
   * @returns {Promise<Object|null>} `{ sessionId, mode, status, metadata, … }` or null
   */
  static async getSessionInfo(sessionId) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/sessions/${sessionId}/info`,
        { credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getSessionInfo:', error);
      return null;
    }
  }

  /**
   * Fetch the active (unworked, unexpired) captured intake issue, if any.
   * Sole consumer is the intake-close "Start a conversation" rewire
   * (`ActionRouter.close_intake`); the dashboard card rides the recent-activity
   * feed directly, and the Foundation-completion card is issue-agnostic.
   * @returns {Promise<{sourceSessionId: string, title: string}|null>} The issue, or null.
   */
  static async getActiveIntakeIssue() {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/intake/active-issue`,
        { credentials: 'include' }
      );
      const data = await this.handleResponse(response);
      return data?.issue || null;
    } catch (error) {
      console.error('Network error in getActiveIntakeIssue:', error);
      return null;
    }
  }

  // NOTE: pauseSessionForCheckout() was removed — sessions stay ACTIVE through
  // the Stripe roundtrip on their own (no orphan reaper to evade), and the
  // return path resumes via resumeSession. See session-persistence-and-resume.md §4.6.

  // ===== SESSION NOTES METHODS =====

  /**
   * Get session notes with optional filtering
   * @param {Object} options - Query options
   * @param {string} [options.theme] - Filter by theme
   * @param {string} [options.classification] - Filter by classification (substantive/mixed/light)
   * @param {number} [options.limit=50] - Maximum number of notes to return
   * @param {number} [options.offset=0] - Number of notes to skip
   * @returns {Promise<Object|null>} Session notes with pagination info or null on error
   */
  static async getSessionNotes({ theme, classification, limit = 50, offset = 0 } = {}) {
    try {
      const params = new URLSearchParams();
      if (theme) params.set('theme', theme);
      if (classification) params.set('classification', classification);
      params.set('limit', limit.toString());
      params.set('offset', offset.toString());

      const response = await fetchWithTimeout(
        `${API_URL}/session-notes?${params.toString()}`,
        { credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getSessionNotes:', error);
      return null;
    }
  }

  /**
   * Get session notes by session number (for user-friendly deep linking)
   * @param {number} sessionNumber - Session number (1, 2, 3, etc.)
   * @returns {Promise<Object|null>} Session notes with observations, practices, questions or null on error
   */
  static async getSessionNotesByNumber(sessionNumber) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/sessions/by-number/${sessionNumber}/notes`,
        { credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getSessionNotesByNumber:', error);
      return null;
    }
  }

  /**
   * Mark a session note as viewed, or (pass viewed=false) explicitly un-viewed.
   * @param {string} noteId - Session note ID
   * @param {boolean} [viewed=true] - true to mark viewed, false to un-view
   * @returns {Promise<Object|null>} Updated view metadata or null on error
   */
  static async markSessionNoteViewed(noteId, viewed = true) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/session-notes/${encodeURIComponent(noteId)}/view`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ viewed })
        }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in markSessionNoteViewed:', error);
      return null;
    }
  }

  /**
   * Set or clear a session note's manual unread marker. The sidebar presents
   * this as Mark as read/unread; `flagged` remains the storage/API boundary and
   * is always explicit (no default) — mirrors the route.
   * @param {string} noteId - Session note ID
   * @param {boolean} flagged - true to flag, false to clear
   * @returns {Promise<Object|null>} Updated flag metadata or null on error
   */
  static async flagSessionNote(noteId, flagged) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/session-notes/${encodeURIComponent(noteId)}/flag`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ flagged })
        }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in flagSessionNote:', error);
      return null;
    }
  }

  /**
   * Bulk star / unstar session notes (search select-mode). Returns the parsed
   * envelope — `{ count }` on success, `{ success:false, error:{ code } }` on
   * failure. Never throws.
   * @param {string[]} ids - Session note ids
   * @param {boolean} [starred=true] - true to star, false to unstar
   * @returns {Promise<Object|null>}
   */
  static async starSessionNotes(ids, starred = true) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/session-notes/star`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids, starred })
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in starSessionNotes:', error);
      return null;
    }
  }

  /**
   * Bulk archive / unarchive session notes (search select-mode). Returns the
   * parsed envelope; never throws.
   * @param {string[]} ids - Session note ids
   * @param {boolean} [archived=true] - true to archive, false to unarchive
   * @returns {Promise<Object|null>}
   */
  static async archiveSessionNotes(ids, archived = true) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/session-notes/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids, archived })
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in archiveSessionNotes:', error);
      return null;
    }
  }

  /**
   * Soft-delete a session (search select-mode / detail Delete). Returns the
   * parsed envelope — `{ deleted:true }` on success, `{ success:false,
   * error:{ code } }` on failure (e.g. CANNOT_DELETE_ACTIVE). Never throws.
   * @param {string} sessionId - Session UUID
   * @returns {Promise<Object|null>}
   */
  static async deleteSession(sessionId) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/sessions/${encodeURIComponent(sessionId)}`,
        { method: 'DELETE', credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in deleteSession:', error);
      return null;
    }
  }

  /**
   * Set or clear the SessionNote-backed star for a live chat session.
   * @param {string} sessionId - Session UUID
   * @param {boolean} [starred=true] - Desired star state
   * @returns {Promise<Object|null>} Parsed API result
   */
  static async starSession(sessionId, starred = true) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/sessions/${encodeURIComponent(sessionId)}/star`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ starred })
        }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in starSession:', error);
      return null;
    }
  }

  // ===== PRACTICE METHODS =====

  /**
   * Get practices with optional filtering.
   * @param {Object} options - Query options
   * @param {string} [options.status='all'] - Filter: 'active', 'completed', 'archived', 'all'
   * @param {number} [options.limit=100] - Maximum practices to return
   * @returns {Promise<Array|null>} Array of practices or null on error
   */
  static async getPractices({ status = 'all', limit = 100 } = {}) {
    try {
      const params = new URLSearchParams();
      params.set('status', status);
      params.set('limit', limit.toString());

      const response = await fetchWithTimeout(
        `${API_URL}/practices?${params.toString()}`,
        { credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getPractices:', error);
      return null;
    }
  }

  /**
   * Mark a practice as viewed, or (pass viewed=false) explicitly un-viewed.
   * @param {string} practiceId
   * @param {boolean} [viewed=true] - true to mark viewed, false to un-view
   * @returns {Promise<Object|null>} Updated view metadata or null on error
   */
  static async markPracticeViewed(practiceId, viewed = true) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/practices/${practiceId}/view`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ viewed })
        }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in markPracticeViewed:', error);
      return null;
    }
  }

  /**
   * Archive an active practice (soft-delete; reachable from the right-panel
   * options dropdown, the row kebab, the list's bulk select, and the retired
   * check-in set-aside terminus whose `set_aside_practice` handler stays
   * registered for in-flight transcripts). Throws on non-2xx so callers can
   * surface a toast.
   *
   * The thrown message is prefixed with the envelope's error CODE so callers can
   * distinguish a benign stale click (`PRACTICE_NOT_ARCHIVABLE` — the row was
   * completed or archived in another tab) from a real failure; the error envelope
   * is `{ success, error, code }`, so the code is otherwise lost. Mirrors
   * `keepPracticeOpen`.
   *
   * @param {string} practiceId
   * @returns {Promise<Object>} Updated practice
   */
  static async archivePractice(practiceId) {
    const response = await fetchWithTimeout(
      `${API_URL}/practices/${practiceId}/archive`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      }
    );
    const result = await this.handleResponse(response);
    if (result?.success === false) {
      const message = result?.error || 'Failed to archive experiment';
      throw new Error(result?.code ? `${result.code} ${message}` : message);
    }
    return result;
  }

  /**
   * Extend an active experiment's observation window — the "Keep it open" branch
   * of the report conversation. Throws on non-2xx.
   *
   * `sessionId` is REQUIRED for the report to be logged: the route stamps
   * `metadata.outcome = 'kept_open'` on that session, which is what lets a
   * one-message report ("Didn't happen.") still produce a PracticeLog. It cannot
   * be written by the caller — `outcome` is server-owned and the generic
   * session-metadata PATCH rejects it.
   *
   * The thrown message is prefixed with the envelope's error CODE so callers can
   * distinguish a benign stale click (`PRACTICE_NOT_EXTENDABLE`) from a real
   * failure; the error envelope is `{ success, error, code }`, so the code is
   * otherwise lost.
   *
   * @param {string} practiceId
   * @param {string|null} [sessionId] - The report conversation to stamp
   * @returns {Promise<Object>} Updated practice
   */
  static async keepPracticeOpen(practiceId, sessionId = null) {
    const response = await fetchWithTimeout(
      `${API_URL}/practices/${practiceId}/keep-open`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sessionId })
      }
    );
    const result = await this.handleResponse(response);
    if (result?.success === false) {
      const message = result?.error || 'Failed to keep experiment open';
      throw new Error(result?.code ? `${result.code} ${message}` : message);
    }
    return result;
  }

  /**
   * Restore an archived practice to active. Throws on non-2xx.
   * @param {string} practiceId
   * @returns {Promise<Object>} Updated practice
   */
  static async restorePractice(practiceId) {
    const response = await fetchWithTimeout(
      `${API_URL}/practices/${practiceId}/restore`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      }
    );
    const result = await this.handleResponse(response);
    if (result?.success === false) {
      // `{ success, error, code }` — `error` is the message string, not an object.
      const message = result?.error || 'Failed to restore experiment';
      throw new Error(message);
    }
    return result;
  }

  /**
   * Set advice mode stance (explore or act) on a session.
   *
   * @param {string} sessionId - Session ID
   * @param {string} stance - 'advisor', 'explore', 'act' or 'support'
   * @param {Object} [options]
   * @param {boolean} [options.completeAttunement=false] - When true (bridge
   *   button click during ATTUNEMENT), backend also writes
   *   `attunementCompletedAt`. The toggle handler should NOT pass this flag —
   *   only the bridge button handler does.
   * @param {boolean} [options.voiceCorrection=false] - When true (a voice-
   *   correction pill click, advice-unified-terminus), the backend takes the
   *   success-gated correction path: it validates the window is still open,
   *   commits a `user_explicit` stance + clock reset + correction retirement
   *   atomically, or returns a 409 `CORRECTION_WINDOW_CLOSED` and changes
   *   nothing.
   * @returns {Promise<Object|null>} Result envelope. Success unwraps to
   *   `{ stance, corrected? }`; a 409/failure returns the raw
   *   `{ success:false, code, ... }` error envelope; a network error returns null.
   */
  static async setAdviceStance(sessionId, stance, { completeAttunement = false, voiceCorrection = false } = {}) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/chat/set-stance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sessionId, stance, completeAttunement, voiceCorrection })
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in setAdviceStance:', error);
      return null;
    }
  }

  // ===== EXPLORE QUESTIONS METHODS =====

  /**
   * Get explore questions with optional status filter
   * @param {Object} options - Query options
   * @param {string} [options.status='unexplored'] - Filter: 'unexplored', 'explored', 'dismissed', 'all'
   * @param {number} [options.limit=100] - Maximum number of threads to return
   * @returns {Promise<Array|null>} Array of open threads or null on error
   */
  static async getOpenThreads({ status, limit } = {}) {
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (limit) params.set('limit', limit.toString());

      const response = await fetchWithTimeout(
        `${API_URL}/open-threads?${params.toString()}`,
        { credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getOpenThreads:', error);
      return null;
    }
  }

  /**
   * Update an open thread (mark addressed). Star / archive / delete go through
   * the bulk wrappers below.
   * @param {string} threadId - Thread ID
   * @param {string} action - Action: 'address'
   * @param {string} [addressedInSessionId] - Session ID where thread was addressed (for 'address' action)
   * @returns {Promise<Object|null>} Updated thread or null on error
   */
  static async updateOpenThread(threadId, action, addressedInSessionId) {
    try {
      const body = { action };
      if (addressedInSessionId) body.addressedInSessionId = addressedInSessionId;

      const response = await fetchWithTimeout(
        `${API_URL}/open-threads/${threadId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body)
        }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in updateOpenThread:', error);
      return null;
    }
  }

  /**
   * Bulk star / unstar the caller's own open threads (Open Threads select-mode).
   * Returns the parsed envelope — `{ count }` on success, `{ success:false,
   * error:{ code } }` on failure. Never throws.
   * @param {string[]} ids - OpenThread ids
   * @param {boolean} [starred=true] - true to star, false to unstar
   * @returns {Promise<Object|null>}
   */
  static async starOpenThreads(ids, starred = true) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/open-threads/star`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids, starred })
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in starOpenThreads:', error);
      return null;
    }
  }

  /**
   * Bulk archive / unarchive the caller's own open threads (Open Threads
   * select-mode). Archive reuses the thread's `dismissedAt` stamp. Returns the
   * parsed envelope; never throws.
   * @param {string[]} ids - OpenThread ids
   * @param {boolean} [archived=true] - true to archive, false to unarchive
   * @returns {Promise<Object|null>}
   */
  static async archiveOpenThreads(ids, archived = true) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/open-threads/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids, archived })
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in archiveOpenThreads:', error);
      return null;
    }
  }

  /**
   * Bulk soft-delete the caller's own open threads (Open Threads select-mode).
   * Returns the parsed envelope — `{ count }` on success, error envelope on
   * failure. Never throws.
   * @param {string[]} ids - OpenThread ids
   * @returns {Promise<Object|null>}
   */
  static async deleteOpenThreads(ids) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/open-threads/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids })
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in deleteOpenThreads:', error);
      return null;
    }
  }

  // ===== FRAMEWORK RESULTS METHODS =====

  /**
   * Get the user's completed clinical-framework assessment results
   * (Library page's Assessments tab).
   * @returns {Promise<Array|null>} Array of results ({ questionnaireId,
   *   frameworkCode, title, completedAt, classification, scores, content })
   *   or null on error
   */
  static async getFrameworkResults() {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/discovery/frameworks/results`,
        { credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getFrameworkResults:', error);
      return null;
    }
  }

  /**
   * Get the user's own answered responses to a completed clinical-framework
   * questionnaire, flattened to display strings in canonical question order.
   * Powers the Library assessment detail page's "Your answers" disclosure
   * (lazy-fetched on first expand — see js/chat/assessmentAnswersLoader.js).
   * A valid questionnaire the user never completed returns `{ pairs: [] }`,
   * not an error.
   * @param {string} questionnaireId - Content-registry questionnaire id (e.g. 'bfi20')
   * @returns {Promise<Object|null>} `{ pairs: [{ questionText, answerText, questionType }] }`
   *   on success; an error envelope (`{ success: false, error, code }`) on a
   *   non-2xx response; `null` on a network error
   */
  static async getFrameworkAnswers(questionnaireId) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/discovery/frameworks/${encodeURIComponent(questionnaireId)}/answers`,
        { credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getFrameworkAnswers:', error);
      return null;
    }
  }

  // ===== PATTERNS METHODS =====

  /**
   * Get user patterns with optional status filter
   * @param {Object} options - Query options
   * @param {string} [options.status='active'] - Filter: 'active', 'dismissed', 'archived', 'all'
   * @returns {Promise<Array|null>} Array of patterns or null on error
   */
  static async getPatterns({ status = 'active' } = {}) {
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);

      const response = await fetchWithTimeout(
        `${API_URL}/patterns?${params.toString()}`,
        { credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getPatterns:', error);
      return null;
    }
  }

  /**
   * Get a single pattern with all its observation instances grouped by session
   * @param {string} patternCode - Pattern code (e.g., 'pursue-withdraw')
   * @returns {Promise<Object|null>} Pattern with instances or null on error
   */
  static async getPatternByCode(patternCode) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/patterns/${encodeURIComponent(patternCode)}`,
        { credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getPatternByCode:', error);
      return null;
    }
  }

  /**
   * Get pattern definition with educational content
   * Returns static information about a pattern type (what it is, how it shows up, etc.)
   * @param {string} patternCode - Pattern code (e.g., 'pursue-withdraw')
   * @returns {Promise<Object|null>} Pattern definition with educational content or null on error
   */
  static async getPatternDefinition(patternCode) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/patterns/definition/${encodeURIComponent(patternCode)}`,
        { credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getPatternDefinition:', error);
      return null;
    }
  }

  /**
   * Get the user-facing summary for one taxonomy topic (lazy read-through).
   * @param {string} topicId - topicRegistry id (e.g. 'connection')
   * @returns {Promise<Object|null>} { topicId, hasData, status, summary, cardBlurb, overallCoverage, generatedAt } or null on error
   */
  static async getTopicSummary(topicId) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/topics/${encodeURIComponent(topicId)}/summary`,
        { credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getTopicSummary:', error);
      return null;
    }
  }

  /**
   * Get the card data for all 12 taxonomy topics (cardBlurb + status + coverage),
   * for the Insights topic grid.
   * @returns {Promise<Array|null>} array of { topicId, label, type, cardBlurb, status, overallCoverage, generatedAt, hasData } or null on error
   */
  static async getAllTopicSummaries() {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/topics/summaries`,
        { credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getAllTopicSummaries:', error);
      return null;
    }
  }

  /**
   * Manually regenerate a topic summary (synchronous Sonnet call).
   * @param {string} topicId - topicRegistry id
   * @returns {Promise<Object|null>} the fresh summary or null on error
   */
  static async regenerateTopicSummary(topicId) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/topics/${encodeURIComponent(topicId)}/summary/regenerate`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include' },
        185000 // matches the route's 180s server timeout for the synchronous regen
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in regenerateTopicSummary:', error);
      return null;
    }
  }

  // ===== GUIDES METHODS =====

  /**
   * Get available guide definitions with user's current status for each
   * @returns {Promise<Array|null>} Array of guide availability info or null on error
   */
  static async getAvailableGuides() {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/guides/available`,
        { credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getAvailableGuides:', error);
      return null;
    }
  }

  /**
   * The viewer's shared-guide manifest (D2c/D3) — every couple-scoped shared
   * guide their couple has a readable coordination row for, each serialized
   * through `resolveForViewer` (own receipt only). 404 (flag off, or not in
   * an active couple) and any network error both resolve to `[]` — this
   * method NEVER throws into its caller and NEVER returns null, so
   * `InsightsSection.loadGuidesData` can fetch it alongside
   * `getAvailableGuides()` without any extra guard: own guides always render
   * whether or not this list comes back empty.
   * @returns {Promise<Array>} Shared-guide manifest items, or [] on 404/error
   */
  static async getSharedGuides() {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/discovery/couples/shared-guides`,
        { credentials: 'include' }
      );
      const result = await this.handleResponse(response);
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Network error in getSharedGuides:', error);
      return [];
    }
  }

  /**
   * The viewer's own conjunct of a shared guide's bilateral visibility gate
   * (never a writer of consent, receipts, or the discovery master — see
   * `PATCH /couples/shared-guides/:guideDefinitionId/enabled`).
   * @param {string} guideDefinitionId - Guide definition ID
   * @param {boolean} enabled - Next value of the viewer's own gate conjunct
   * @returns {Promise<Object|null>} Updated shared-guide manifest item, or null on error
   */
  static async setSharedGuideEnabled(guideDefinitionId, enabled) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/discovery/couples/shared-guides/${encodeURIComponent(guideDefinitionId)}/enabled`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ enabled })
        }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in setSharedGuideEnabled:', error);
      return null;
    }
  }

  /**
   * Record that the viewer has read the couple's half of a guide, or (pass
   * viewed=false) explicitly un-read it. Writes the viewer's own participant
   * row only and answers with metadata alone — `{guideDefinitionId, viewedAt}`
   * — never the shared document.
   *
   * Like `markGuideViewed`, this NEVER throws into its caller: a 404 (flag off,
   * not in a couple, no readable shared guide) resolves through
   * `handleResponse` to the error envelope and a network failure to null. The
   * caller must treat a missing `viewedAt` as "not recorded" and leave the
   * unread state alone rather than clearing it optimistically.
   * @param {string} guideDefinitionId - Guide definition ID
   * @param {boolean} [viewed=true] - true to mark viewed, false to un-view
   * @returns {Promise<Object|null>} `{guideDefinitionId, viewedAt}`, the error
   *   envelope on a refused request, or null on a network error
   */
  static async markSharedGuideViewed(guideDefinitionId, viewed = true) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/discovery/couples/shared-guides/${encodeURIComponent(guideDefinitionId)}/view`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ viewed })
        }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in markSharedGuideViewed:', error);
      return null;
    }
  }

  /**
   * Archive, or restore, the viewer's own half of a shared guide. Archive is
   * per person: this never touches the partner's row and the response carries
   * metadata alone — `{guideDefinitionId, archivedAt}`.
   *
   * Never throws into its caller (same envelope/null contract as
   * `setSharedGuideEnabled`), so a caller archiving both halves of a merged
   * guide card must check the result before patching local state.
   * @param {string} guideDefinitionId - Guide definition ID
   * @param {boolean} archived - true to archive, false to restore
   * @returns {Promise<Object|null>} `{guideDefinitionId, archivedAt}`, the error
   *   envelope on a refused request, or null on a network error
   */
  static async setSharedGuideArchived(guideDefinitionId, archived) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/discovery/couples/shared-guides/${encodeURIComponent(guideDefinitionId)}/archived`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ archived })
        }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in setSharedGuideArchived:', error);
      return null;
    }
  }

  /**
   * Get a specific guide with full content
   * @param {string} guideDefinitionId - Guide definition ID (e.g., 'guide-communication')
   * @returns {Promise<Object|null>} Guide with content or null on error
   */
  static async getGuide(guideDefinitionId) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/guides/${encodeURIComponent(guideDefinitionId)}`,
        { credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getGuide:', error);
      return null;
    }
  }

  /**
   * Generate a guide, or complete one that never reached READY (ERROR / stale
   * GENERATING). A READY guide is immutable, so this is a no-op for it.
   * @param {string} guideDefinitionId - Guide definition ID
   * @returns {Promise<Object|null>} Generated guide or null on error
   */
  static async generateGuide(guideDefinitionId) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/guides/${encodeURIComponent(guideDefinitionId)}/generate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({})
        },
        120000 // 120s — guide generation is an 8KB-input Sonnet call
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in generateGuide:', error);
      return null;
    }
  }

  /**
   * Bulk star / unstar guides, keyed by guideDefinitionId (NOT the UserGuide
   * row UUID). Same `(ids, flag=true)` shape as `starSessionNotes`. Returns
   * the parsed envelope — `{ count }` on success, `{ success:false, error }`
   * on failure. Never throws.
   * @param {string[]} ids - Guide definition ids
   * @param {boolean} [starred=true] - true to star, false to unstar
   * @returns {Promise<Object|null>}
   */
  static async starGuides(ids, starred = true) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/guides/star`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids, starred })
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in starGuides:', error);
      return null;
    }
  }

  /**
   * Bulk archive / unarchive guides, keyed by guideDefinitionId. Returns the
   * parsed envelope; never throws.
   * @param {string[]} ids - Guide definition ids
   * @param {boolean} [archived=true] - true to archive, false to unarchive
   * @returns {Promise<Object|null>}
   */
  static async archiveGuides(ids, archived = true) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/guides/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids, archived })
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in archiveGuides:', error);
      return null;
    }
  }

  /**
   * Mark a guide as viewed, or (pass viewed=false) explicitly un-viewed.
   * @param {string} guideDefinitionId - Guide definition ID
   * @param {boolean} [viewed=true] - true to mark viewed, false to un-view
   * @returns {Promise<Object|null>} Updated guide or null on error
   */
  static async markGuideViewed(guideDefinitionId, viewed = true) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/guides/${encodeURIComponent(guideDefinitionId)}/view`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ viewed })
        }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in markGuideViewed:', error);
      return null;
    }
  }

  /**
   * Accept a draft practice — flips status 'draft' → 'active'.
   * Idempotent on already-active practices.
   *
   * `checkInIntervalDays` carries the observation window the user picked on the
   * proposal card's chip row. With none, the request is byte-identical to the
   * bodiless call and the backend resolves the window off the row.
   *
   * @param {string} practiceId
   * @param {number} [checkInIntervalDays] - Chosen window length in days (1–90)
   * @returns {Promise<Object|null>}
   */
  static async acceptPractice(practiceId, checkInIntervalDays) {
    try {
      const hasInterval = checkInIntervalDays !== undefined && checkInIntervalDays !== null;
      const response = await fetchWithTimeout(
        `${API_URL}/practices/${encodeURIComponent(practiceId)}/accept`,
        hasInterval
          ? {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ checkInIntervalDays })
          }
          : { method: 'POST', credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in acceptPractice:', error);
      return null;
    }
  }

  /**
   * Record a practice completion confirmation decision from action buttons.
   * @param {string} practiceId
   * @param {string} sessionId
   * @param {'confirm'|'decline'} decision
   * @returns {Promise<Object|null>}
   */
  static async practiceCompletionDecision(practiceId, sessionId, decision) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/practices/${encodeURIComponent(practiceId)}/completion-decision`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ sessionId, decision })
        }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in practiceCompletionDecision:', error);
      return null;
    }
  }

  /**
   * Downgrade an active orientation-advice session to a normal advice session
   * in place. Flips `metadata.entryType` from `'orientation_advice'` to
   * `'normal'` and clears `practiceProposalState` if still `'proposed'`.
   * Same session row, same chat history — only metadata changes, so the next
   * turn flows through the vanilla advice path with full context and tools.
   *
   * Used by the orientation off-topic deflection "Discuss something else"
   * button. Must complete before the synthetic `[scope_switched]` user
   * message fires.
   *
   * @param {string} sessionId
   * @returns {Promise<Object|null>} { sessionId, metadata } on success, null on network error
   */
  static async downgradeOrientationScope(sessionId) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/sessions/${encodeURIComponent(sessionId)}/scope/downgrade-orientation`,
        { method: 'POST', credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in downgradeOrientationScope:', error);
      return null;
    }
  }

  /**
   * Flip an active SCOPING-phase advice session in-place into the
   * `memo_compose` entry (plan: advice-scoping-routing.md Phase R2 — typed
   * "let's write a note" intent). Same session, same history — only the
   * metadata changes; the agent's next turn opens the memo draft directly.
   *
   * Returns the parsed response envelope on both success and a handled failure
   * (e.g. 400 `NOTE_COMPOSE_NOT_COUPLED`) so the caller can check
   * `result.success` and surface `result.error.message` — this NEVER throws.
   * Only a genuine network error returns `null`.
   *
   * @param {string} sessionId
   * @returns {Promise<Object|null>} `{ sessionId, metadata }` on success, the parsed error envelope on a handled failure, or null on network error
   */
  static async startNoteCompose(sessionId) {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/sessions/${encodeURIComponent(sessionId)}/scope/start-note-compose`,
        { method: 'POST', credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in startNoteCompose:', error);
      return null;
    }
  }

  /**
   * Bulk star / unstar practices (search + practices select-mode). Returns the
   * parsed envelope; never throws.
   * @param {string[]} ids - Practice ids (UUIDs)
   * @param {boolean} [starred=true] - true to star, false to unstar
   * @returns {Promise<Object|null>}
   */
  static async starPractices(ids, starred = true) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/practices/star`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids, starred })
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in starPractices:', error);
      return null;
    }
  }

  /**
   * Hard-delete a practice of any status (cascades to its check-in history).
   *
   * `checkInIntervalDays` carries the observation window the user had chosen
   * on the proposal card's chip row at the moment of rejection, so the
   * reject-seam snapshot records what the user was actually looking at
   * rather than the row's original resolved value. Sent as a query param
   * (not a DELETE body — some proxies strip those) alongside `draftOnly`.
   * Ignored server-side outside the draft-reject path, same as its
   * `draftOnly` sibling.
   *
   * @param {string} practiceId
   * @param {Object} [options]
   * @param {boolean} [options.draftOnly=false] - Delete only while still a draft proposal
   * @param {number} [options.checkInIntervalDays] - Chosen window length in days (1–90)
   * @returns {Promise<Object|null>}
   */
  static async deletePractice(practiceId, { draftOnly = false, checkInIntervalDays } = {}) {
    try {
      const params = new URLSearchParams();
      if (draftOnly) params.set('draftOnly', 'true');
      if (checkInIntervalDays !== undefined && checkInIntervalDays !== null) {
        params.set('checkInIntervalDays', String(checkInIntervalDays));
      }
      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await fetchWithTimeout(
        `${API_URL}/practices/${encodeURIComponent(practiceId)}${query}`,
        { method: 'DELETE', credentials: 'include' }
      );
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in deletePractice:', error);
      return null;
    }
  }
}
